/** A refusal to voice a text at all (client.ts TtsRefusedError): no fallback voice may speak it. */
export function speechRefused(error) {
    return Boolean(error) && typeof error === 'object' && error.ttsRefused === true;
}
/** Speech has its own cancellation lifetime; stopping it must never cancel the chat or change mode. */
export class ReplySpeech {
    dependencies;
    generation = 0;
    pending = null;
    constructor(dependencies) {
        this.dependencies = dependencies;
    }
    stop() {
        this.generation++;
        this.pending?.abort();
        this.pending = null;
        try {
            this.dependencies.stopAudio();
        }
        catch { /* Already released by the browser. */ }
        try {
            this.dependencies.stopFallback();
        }
        catch { /* Speech synthesis may be unavailable. */ }
        this.dependencies.onState('idle');
    }
    async speak(text) {
        this.stop();
        if (!text.trim())
            return;
        const generation = this.generation;
        const pending = new AbortController();
        this.pending = pending;
        const current = () => this.generation === generation && this.pending === pending && !pending.signal.aborted;
        const ended = () => {
            if (!current())
                return;
            this.pending = null;
            this.dependencies.onState('idle');
        };
        this.dependencies.onState('loading');
        let blob = null;
        try {
            blob = await this.dependencies.requestAudio(text, pending.signal);
        }
        catch (error) {
            // The service refused to voice this text (an answer from a chat that holds pay data) or held it back (its 204): no
            // voice speaks it, not even the browser's. Anything else: an unavailable TTS provider may use the browser voice,
            // unless cancelled.
            if (speechRefused(error)) {
                if (current()) {
                    this.pending = null;
                    this.dependencies.onState('idle');
                }
                return;
            }
        }
        if (!current())
            return;
        if (blob) {
            let result;
            try {
                result = await this.dependencies.playAudio(blob, { signal: pending.signal, onEnded: ended });
            }
            catch {
                result = { ok: false, reason: 'error' };
            }
            if (!current())
                return;
            if (result.ok) {
                this.dependencies.onState('playing');
                return;
            }
            if (result.reason === 'cancelled') {
                ended();
                return;
            }
        }
        if (!current())
            return;
        this.dependencies.onState('playing');
        try {
            if (!this.dependencies.speakFallback(text, ended))
                ended();
        }
        catch {
            ended();
        }
    }
}
//# sourceMappingURL=replySpeech.js.map